import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Camera, X, RotateCcw, Check } from 'lucide-react';
import { toast } from 'sonner';

const CameraCapture = ({ isOpen, onClose, onCapture, attendanceType, previewZoom = 1, requireRearCapture = false }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [frontPhotoBlob, setFrontPhotoBlob] = useState(null);
  const [captureStep, setCaptureStep] = useState('front');
  const [isCapturing, setIsCapturing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setCaptureStep('front');
      setFrontPhotoBlob(null);
      setUploadProgress(0);
      startCamera('user');
      setIsConfirming(false);
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isOpen]);

  // Re-attach stream to video element when retaking photo
  useEffect(() => {
    if (videoRef.current && stream && !capturedImage) {
      videoRef.current.srcObject = stream;
    }
  }, [capturedImage, stream]);

  useEffect(() => {
    if (!(captureStep === 'rear' && isConfirming)) return;

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) return prev;
        const increment = Math.max(3, Math.floor(Math.random() * 8));
        return Math.min(90, prev + increment);
      });
    }, 200);

    return () => clearInterval(interval);
  }, [captureStep, isConfirming]);

  const startCamera = async (facingMode) => {
    try {
      setError(null);
      setIsCapturing(false);
      setCapturedImage(null);

      // Request camera permissions - prefer front camera for employee photos
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 640 },
          height: { ideal: 480 },
          aspectRatio: { ideal: 4/3 }
        },
        audio: false
      });

      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setError(getErrorMessage(err));
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const getErrorMessage = (err) => {
    if (err.name === 'NotAllowedError') {
      return 'Camera permission denied. Please allow camera access and try again.';
    } else if (err.name === 'NotFoundError') {
      return 'No camera found on this device.';
    } else if (err.name === 'NotSupportedError') {
      return 'Camera is not supported on this device.';
    } else {
      return 'Failed to access camera. Please check your camera settings.';
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    if (requireRearCapture && captureStep === 'rear' && isConfirming) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (requireRearCapture && captureStep === 'rear') {
      setIsConfirming(true);
      setUploadProgress(0);
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob with medium quality (as per user request)
    canvas.toBlob(async (blob) => {
      if (blob) {
        if (requireRearCapture && captureStep === 'rear') {
          try {
            setUploadProgress((prev) => Math.max(prev, 95));
            await onCapture(frontPhotoBlob, blob);
            setUploadProgress(100);
          } finally {
            setIsConfirming(false);
          }
          return;
        }

        const imageUrl = URL.createObjectURL(blob);
        setCapturedImage({ blob, url: imageUrl });
        setIsCapturing(true);
      }
    }, 'image/jpeg', 0.7); // 0.7 quality = medium quality
  };

  const retakePhoto = () => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage.url);
      setCapturedImage(null);
    }
    setIsCapturing(false);
  };

  const confirmPhoto = async () => {
    if (capturedImage) {
      if (requireRearCapture && captureStep === 'front') {
        setIsConfirming(true);
        setFrontPhotoBlob(capturedImage.blob);
        URL.revokeObjectURL(capturedImage.url);
        setCapturedImage(null);
        setIsCapturing(false);
        setCaptureStep('rear');
        await startCamera('environment');
        setIsConfirming(false);
        return;
      }

      setIsConfirming(true);
      await onCapture(frontPhotoBlob || capturedImage.blob, requireRearCapture ? capturedImage.blob : null);
    }
  };

  const handleClose = () => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage.url);
      setCapturedImage(null);
    }
    setIsCapturing(false);
    setFrontPhotoBlob(null);
    setCaptureStep('front');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Camera className="h-5 w-5 mr-2" />
            {captureStep === 'rear'
              ? 'Uploading Photo with Consent'
              : `Take ${attendanceType === 'time_in' ? 'Time In' : 'Time Out'} Photo`}
          </DialogTitle>
          <DialogDescription className="sr-only">Camera capture dialog for attendance photo</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error ? (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="text-center space-y-3">
                  <div className="text-red-600">
                    <Camera className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{error}</p>
                  </div>
                  <div className="space-y-2">
                    <Button 
                      onClick={() => startCamera(captureStep === 'rear' ? 'environment' : 'user')} 
                      size="sm" 
                      className="w-full"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Camera Preview or Captured Image */}
              <div className="relative bg-gray-100 rounded-lg overflow-hidden aspect-[4/3]">
                {captureStep === 'rear' ? (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="absolute inset-0 w-full h-full object-cover opacity-0"
                    />
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                      <p className="text-sm text-gray-700 text-center leading-relaxed">
                        The photo you are about to upload serves as official verification of your attendance. Any falsification, modification, or tampering of this file is considered a violation of company policy and may lead to disciplinary action, including consequences that may affect your employment.
                      </p>
                    </div>
                  </>
                ) : capturedImage ? (
                  <img 
                    src={capturedImage.url} 
                    alt="Captured" 
                    className="w-full h-full object-cover"
                    style={{ transform: `scale(${previewZoom})`, transformOrigin: 'center' }}
                  />
                ) : (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    style={{ transform: `scale(${previewZoom})`, transformOrigin: 'center' }}
                  />
                )}
                
                {/* Overlay guide */}
                {!capturedImage && captureStep === 'front' && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="border-2 border-white border-dashed rounded-full w-48 h-48 flex items-center justify-center">
                      <div className="text-white text-center">
                        <Camera className="h-8 w-8 mx-auto mb-2" />
                        <p className="text-sm">Position your face here</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Canvas for photo capture (hidden) */}
              <canvas 
                ref={canvasRef} 
                style={{ display: 'none' }} 
              />

              {/* Action Buttons */}
              <div className="flex space-x-2">
                {capturedImage ? (
                  <>
                    {!(requireRearCapture && captureStep === 'rear') && (
                      <Button 
                        onClick={retakePhoto} 
                        variant="outline" 
                        className="flex-1"
                        disabled={isConfirming}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Retake
                      </Button>
                    )}
                    <Button 
                      onClick={confirmPhoto} 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      disabled={isConfirming}
                    >
                      {isConfirming ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          {requireRearCapture && captureStep === 'front'
                            ? 'Next'
                            : requireRearCapture && captureStep === 'rear'
                              ? 'Uploading...'
                              : 'Use Photo'}
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      onClick={capturePhoto} 
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      disabled={!stream || isConfirming}
                    >
                      {captureStep === 'rear' ? 'Yes, I agree' : (
                        <>
                          <Camera className="h-4 w-4 mr-2" />
                          Capture
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>

              {/* Instructions */}
              <div className="text-center text-sm text-gray-500">
                {captureStep === 'rear' ? (
                  isConfirming ? (
                    <div className="space-y-2">
                      <p>Uploading... {uploadProgress}%</p>
                      <div className="h-2 w-full bg-gray-200 rounded overflow-hidden">
                        <div
                          className="h-full bg-indigo-600 transition-all duration-200"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <p>Please read the notice and continue.</p>
                  )
                ) : capturedImage ? (
                  <p>Review your photo and confirm to proceed</p>
                ) : (
                  <p>Position your face in the circle and click capture</p>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CameraCapture;
